/**
 * The campaign's topics for the window, in the order the route ranked them:
 * by critical weight for what weighs against, by net sentiment for the
 * per-topic reading.
 *
 * The topics are the Tema themes the organisation's comment intelligence
 * classified comments on, each with its sub-themes nested. Themes too small to
 * rank are one "outros" line, and the list shows a page at a time. A theme
 * under the k-gate is only counted, and a count above zero adds one line saying
 * small topics are hidden. A card with nothing to show is left out of the page.
 */

import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { count } from '../../../chrome/measure/format';
import { QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { Button } from '../../../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../ui/collapsible';
import { useTopics, type Window } from '../api/queries';
import type { ThemeRow, Topics } from '../api/schemas';
import { st2, st2Plural } from './messages';
import { commentsLabel, ScoreRow } from './RegionCard';
import { SentimentCard } from './SentimentCard';

const ORDER = { against: 'critical_weight_desc', by: 'net_sentiment_desc' } as const;

/** Rows shown before "ver mais", and how many more each press reveals. */
const PAGE = 6;

interface Entry {
  key: string;
  label: string;
  comments: number;
  score: number;
  children: readonly { key: string; label: string; comments: number; score: number }[];
}

function fromTheme(theme: ThemeRow): Entry {
  return {
    key: theme.value_id,
    label: theme.name,
    comments: theme.comment_count,
    score: theme.net_score,
    children: theme.children.map((child) => ({
      key: child.value_id,
      label: child.name,
      comments: child.comment_count,
      score: child.net_score,
    })),
  };
}

function EntryRow(props: { entry: Entry }): ReactNode {
  const { entry } = props;
  if (entry.children.length === 0) {
    return (
      <ScoreRow label={entry.label} detail={commentsLabel(entry.comments)} value={entry.score} />
    );
  }
  return (
    <Collapsible asChild>
      <li aria-label={entry.label} className="flex flex-col">
        <ScoreRow
          label={entry.label}
          detail={commentsLabel(entry.comments)}
          value={entry.score}
          as="div"
        />
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground group -mt-1 self-start px-0"
          >
            {st2Plural('topics.subthemes.other', entry.children.length, {
              n: count(entry.children.length, getLocale()),
            })}
            <ChevronDown aria-hidden className="size-4 transition-transform group-open-rotate" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="border-border ml-3 flex flex-col divide-y border-l pl-3">
            {entry.children.map((child) => (
              <ScoreRow
                key={child.key}
                label={child.label}
                detail={commentsLabel(child.comments)}
                value={child.score}
              />
            ))}
          </ul>
        </CollapsibleContent>
      </li>
    </Collapsible>
  );
}

export function TopicsView(props: {
  entries: readonly Entry[];
  others?: Topics['others'];
  withheld: number;
}): ReactNode {
  const [shown, setShown] = useState(PAGE);
  const { entries, others, withheld } = props;
  const rest = entries.length - shown;
  return (
    <>
      <ul className="flex flex-col divide-y">
        {entries.slice(0, shown).map((entry) => (
          <EntryRow key={entry.key} entry={entry} />
        ))}
      </ul>
      {rest > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setShown((current) => current + PAGE)}
        >
          {st2('topics.more', { n: count(rest, getLocale()) })}
        </Button>
      )}
      {others !== undefined && others.topics > 0 && (
        <p data-numeric className="text-caption text-muted-foreground">
          {st2Plural('topics.others.other', others.topics, {
            n: count(others.topics, getLocale()),
            comments: commentsLabel(others.comment_count),
          })}
        </p>
      )}
      {withheld > 0 && (
        <p className="text-caption text-muted-foreground">{st2('topics.withheld')}</p>
      )}
    </>
  );
}

/** The themes the card lists; for `against`, only those with a hostile comment,
 *  since one without does not weigh against the campaign whatever its rank. */
function entriesOf(kind: 'against' | 'by', data: Topics): Entry[] {
  return data.themes
    .filter((theme) => kind === 'by' || theme.hostile > 0)
    .map((theme) => fromTheme(theme));
}

export function TopicCard(props: {
  kind: 'against' | 'by';
  campaignId: string;
  tenantId: string;
  window: Window;
}): ReactNode {
  const query = useTopics(props.campaignId, props.tenantId, {
    window: props.window,
    order: ORDER[props.kind],
  });
  // The "outros" line sums what the whole ranking left out, so it belongs to
  // the per-topic card, not the one filtered to hostile rows.
  const othersOf = (data: Topics) => (props.kind === 'by' ? data.others : undefined);
  if (query.status === 'success') {
    const entries = entriesOf(props.kind, query.data);
    const folded = props.kind === 'by' ? query.data.others.topics : 0;
    if (
      entries.length === 0 &&
      folded === 0 &&
      (props.kind === 'against' || query.data.withheld === 0)
    ) {
      return null;
    }
  }
  return (
    <SentimentCard title={st2(`topics.${props.kind}`)}>
      <QueryStates query={query}>
        {(data) => (
          <TopicsView
            entries={entriesOf(props.kind, data)}
            others={othersOf(data)}
            withheld={data.withheld}
          />
        )}
      </QueryStates>
    </SentimentCard>
  );
}
